import { FormGroup } from '@angular/forms';
import { FormGroupRules } from '@shared/models/interfaces/form-group-rules.model';
import { addRemoveError, getControl } from '@shared/helpers/form.helper';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';
import { isEqual } from 'lodash-es';
import { INCONSISTENT_SELECTION, REQUIRED_STATUS } from '@shared/constants/shared.constants';
import { formPaths } from '@shared/constants/form-paths.const';
import { toNum } from '@shared/helpers/math.helper';
import {
    MyAllElemLiquidatoriEntity,
    MyAllElemRevisoriControlloContabileEntity,
    MySpdElemConsiglioGestioneEntity,
    MySpdElemConsiglioSorveglianzaEntity,
} from '../../../../models/interfaces/revisione.interface';
import {
    checkCkNormeValue,
    checkCkNormeValueRequired,
    getAmmControlloGroup,
    getNormeSpaDualisticoControl,
    normeSpaDualisticoCkHelper,
    setRequiredIfNormeActiveAndSecondFilled,
} from './helpers/norme-spa-dualistico-form-validators.helper';

export const normeSpaDualisticoAssembleaRules: FormGroupRules = {
    my_CkSpdCoop9soci: (form: FormGroup) => {
        checkCkNormeValueRequired(form, 'my_NormeSpaDualistico.my_CkSpdCoop9soci');
    },

    my_CkSpdAssembNominSorv: (form: FormGroup) => {
        checkCkNormeValueRequired(form, 'my_NormeSpaDualistico.my_CkSpdAssembNominSorv');
    },

    my_CkSpdAssembDetCompSorv: (form: FormGroup) => {
        checkCkNormeValueRequired(form, 'my_NormeSpaDualistico.my_CkSpdAssembDetCompSorv');
    },

    my_CkSpdAssembDelibDistribUtil: (form: FormGroup) => {
        checkCkNormeValueRequired(form, 'my_NormeSpaDualistico.my_CkSpdAssembDelibDistribUtil');
    },

    my_CkSpdMaggSorvSoci: (form: FormGroup) => {
        setRequiredIfNormeActiveAndSecondFilled(
            form,
            'my_NormeSpaDualistico.my_CkSpdMaggSorvSoci',
            'my_NormeSpaDualistico.my_SpdConsiglioSorveglianza.my_SpdElemConsiglioSorveglianza'
        );
    },

    my_CkSpdNominaConsGest: (form: FormGroup) => {
        setRequiredIfNormeActiveAndSecondFilled(
            form,
            'my_NormeSpaDualistico.my_CkSpdNominaConsGest',
            'my_NormeSpaDualistico.my_SpdConsiglioSorveglianza.my_SpdElemConsiglioSorveglianza'
        );
    },

    my_CkSpdApprovaBilEserc: (form: FormGroup) => {
        setRequiredIfNormeActiveAndSecondFilled(
            form,
            'my_NormeSpaDualistico.my_CkSpdApprovaBilEserc',
            'my_NormeSpaDualistico.my_SpdConsiglioSorveglianza.my_SpdElemConsiglioSorveglianza'
        );
    },

    my_CkSpdRelazCaratMut: (form: FormGroup) => {
        setRequiredIfNormeActiveAndSecondFilled(
            form,
            'my_NormeSpaDualistico.my_CkSpdRelazCaratMut',
            'my_NormeSpaDualistico.my_SpdConsiglioSorveglianza.my_SpdElemConsiglioSorveglianza'
        );
    },

    my_CkSpdVigila: (form: FormGroup) => {
        setRequiredIfNormeActiveAndSecondFilled(form, 'my_NormeSpaDualistico.my_CkSpdVigila', 'my_NormeSpaDualistico.my_SpdConsiglioSorveglianza.my_SpdElemConsiglioSorveglianza');
    },

    my_CkSpdRiferisceAssemb: (form: FormGroup) => {
        setRequiredIfNormeActiveAndSecondFilled(
            form,
            'my_NormeSpaDualistico.my_CkSpdRiferisceAssemb',
            'my_NormeSpaDualistico.my_SpdConsiglioSorveglianza.my_SpdElemConsiglioSorveglianza'
        );
    },

    my_LabelConsiglioSorveglianza: (form: FormGroup) => {
        const ammControlloGroup = getAmmControlloGroup(form);
        const my_LabelConsiglioSorveglianzaControl = getControl(ammControlloGroup, 'my_NormeSpaDualistico.my_LabelConsiglioSorveglianza');
        const my_SpdElemConsiglioSorveglianza: MySpdElemConsiglioSorveglianzaEntity[] = getControl(
            ammControlloGroup,
            'my_NormeSpaDualistico.my_SpdConsiglioSorveglianza.my_SpdElemConsiglioSorveglianza'
        ).value;
        const errors = my_LabelConsiglioSorveglianzaControl.errors ?? {};

        addRemoveError(checkCkNormeValue(form) && !my_SpdElemConsiglioSorveglianza?.length, errors, 'my_LabelConsiglioSorveglianza', {
            status: 'Inserire il Consiglio di sorveglianza',
        });

        my_LabelConsiglioSorveglianzaControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_CkSPDMembroRevisore: (form: FormGroup) => {
        setRequiredIfNormeActiveAndSecondFilled(
            form,
            'my_NormeSpaDualistico.my_gruppo5.my_CkSPDMembroRevisore',
            'my_NormeSpaDualistico.my_SpdConsiglioSorveglianza.my_SpdElemConsiglioSorveglianza'
        );
    },

    my_CkSpdCompElettiStrFinan: (form: FormGroup) => {
        setRequiredIfNormeActiveAndSecondFilled(
            form,
            'my_NormeSpaDualistico.my_CkSpdCompElettiStrFinan',
            'my_NormeSpaDualistico.my_SpdConsiglioSorveglianza.my_SpdElemConsiglioSorveglianza'
        );
    },

    my_LabelConsiglioGestione: (form: FormGroup) => {
        const ammControlloGroup = getAmmControlloGroup(form);
        const my_LabelConsiglioGestioneControl = getControl(ammControlloGroup, 'my_NormeSpaDualistico.my_LabelConsiglioGestione');
        const my_SpdElemConsiglioGestione: MySpdElemConsiglioGestioneEntity[] = getControl(
            ammControlloGroup,
            'my_NormeSpaDualistico.my_SpdConsiglioGestione.my_SpdElemConsiglioGestione'
        ).value;
        const my_AllElemLiquidatori: MyAllElemLiquidatoriEntity[] = getControl(ammControlloGroup, 'my_AllLiquidatori.my_AllElemLiquidatori').value;
        const errors = my_LabelConsiglioGestioneControl.errors ?? {};

        addRemoveError(checkCkNormeValue(form) && !my_SpdElemConsiglioGestione?.length && !my_AllElemLiquidatori?.length, errors, 'my_LabelConsiglioGestione', {
            status: 'Inserire il Consiglio di gestione',
        });

        my_LabelConsiglioGestioneControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_CkSpdCompElettiLimite: (form: FormGroup) => {
        const my_SpdElemConsiglioSorveglianza: MySpdElemConsiglioSorveglianzaEntity[] = getControl(
            form,
            `${formPaths.ammControllo}.my_NormeSpaDualistico.my_SpdConsiglioSorveglianza.my_SpdElemConsiglioSorveglianza`
        ).value;
        normeSpaDualisticoCkHelper(
            form,
            'my_NormeSpaDualistico.my_CkSpdCompElettiLimite',
            'my_NormeSpaDualistico.my_CkSpdCompElettiStrFinan',
            1,
            formPaths.ammControllo,
            !isNilOrEmpty(my_SpdElemConsiglioSorveglianza)
        );
    },

    my_CkSpdAffidamContrCont: (form: FormGroup) => {
        const ammControlloGroup = getAmmControlloGroup(form);
        const my_CkSpdAffidamContrContControl = getControl(ammControlloGroup, 'my_NormeSpaDualistico.my_CkSpdAffidamContrCont');
        const my_CkSpdAffidamContrCont: string = my_CkSpdAffidamContrContControl.value;
        const my_SpdElemConsiglioSorveglianza: MySpdElemConsiglioSorveglianzaEntity[] = getControl(
            ammControlloGroup,
            'my_NormeSpaDualistico.my_SpdConsiglioSorveglianza.my_SpdElemConsiglioSorveglianza'
        ).value;
        const my_AllElemRevisoriControlloContabile: MyAllElemRevisoriControlloContabileEntity[] = getControl(
            ammControlloGroup,
            'my_AllRevisoriControlloContabile.my_AllElemRevisoriControlloContabile'
        ).value;
        const errors = my_CkSpdAffidamContrContControl.errors ?? {};
        const conteggio = my_AllElemRevisoriControlloContabile?.length;

        addRemoveError(checkCkNormeValue(form) && my_CkSpdAffidamContrCont === '4' && isNilOrEmpty(my_SpdElemConsiglioSorveglianza), errors, 'my_CkSpdAffidamContrCont', {
            status: 'Il Consiglio di Sorveglianza è obbligatorio quando gli è stato affidato il Controllo contabile',
        });

        addRemoveError(checkCkNormeValue(form) && my_CkSpdAffidamContrCont === '3' && conteggio <= 1, errors, 'my_CkSpdAffidamContrCont', {
            status: 'Inserire i revisori esterni',
        });

        addRemoveError(checkCkNormeValue(form) && my_CkSpdAffidamContrCont === '2' && conteggio !== 1, errors, 'my_CkSpdAffidamContrCont', {
            status: 'Inserire un revisore esterno',
        });

        addRemoveError(checkCkNormeValue(form) && isNilOrEmpty(my_CkSpdAffidamContrCont), errors, 'my_CkSpdAffidamContrCont', {
            status: REQUIRED_STATUS,
        });

        my_CkSpdAffidamContrContControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_SpdScadenzaAmministratori: (form: FormGroup) => {
        const my_SpdScadenzaAmministratoriControl = getNormeSpaDualisticoControl(form, 'my_SpdScadenzaAmministratori');
        const errors = my_SpdScadenzaAmministratoriControl.errors ?? {};

        addRemoveError(checkCkNormeValue(form) && toNum(my_SpdScadenzaAmministratoriControl?.value) === 99, errors, 'my_SpdScadenzaAmministratori', {
            status: INCONSISTENT_SELECTION,
        });

        addRemoveError(checkCkNormeValue(form) && isNilOrEmpty(my_SpdScadenzaAmministratoriControl?.value), errors, 'my_SpdScadenzaAmministratori', {
            status: REQUIRED_STATUS,
        });

        my_SpdScadenzaAmministratoriControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_SpdNoteComposAssemb: (form: FormGroup) => {
        checkCkNormeValueRequired(form, 'my_NormeSpaDualistico.my_SpdNoteComposAssemb');
    },

    my_SpdNoteComposConsSorv: (form: FormGroup) => {
        setRequiredIfNormeActiveAndSecondFilled(
            form,
            'my_NormeSpaDualistico.my_SpdNoteComposConsSorv',
            'my_NormeSpaDualistico.my_SpdConsiglioSorveglianza.my_SpdElemConsiglioSorveglianza'
        );
    },

    my_SpdNoteComposConsGest: (form: FormGroup) => {
        setRequiredIfNormeActiveAndSecondFilled(
            form,
            'my_NormeSpaDualistico.my_SpdNoteComposConsGest',
            'my_NormeSpaDualistico.my_SpdConsiglioGestione.my_SpdElemConsiglioGestione',
            formPaths.ammControllo,
            'Il campo deve essere inserito obbligatoriamente.'
        );
    },

    my_SpdNoteComposContrContab: (form: FormGroup) => {
        checkCkNormeValueRequired(form, 'my_NormeSpaDualistico.my_SpdNoteComposContrContab');
    },
};
